import React from 'react';
import V2Layout from './Layout';
import { Box, Button, Card, Grid, IconButton, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField } from '@mui/material';
import StyledTableRow from '../../components/v2/common/table/StyledTableRow';
import { translate } from '../../i18n/customI18nProvider';
import { deleteMyposTerminal, getMyposTerminalList } from '../../services/mypos_terminal.service';
import { visuallyHidden } from '@mui/utils';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Swal from 'sweetalert2';
import { convertMessageCodeToMessage } from '../../util';
import config from '../../config';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DangerousIcon from '@mui/icons-material/Dangerous';

const tableHeader = [
    {
        id: 'terminal_code',
        key: 'terminal_code',
        align: 'left',
        disablePadding: false,
        label: translate('custom.terminal_code'),
        sortable: true
    },
    {
        id: 'terminal_id',
        key: 'terminal_id',
        align: 'left',
        disablePadding: false,
        label: translate('custom.terminal_id'),
        sortable: true
    },
    {
        id: 'is_paired',
        key: 'is_paired',
        align: 'center',
        disablePadding: false,
        label: translate('custom.is_paired'),
        sortable: true
    },
    {
        id: 'actions',
        key: 'actions',
        align: 'left',  
        disablePadding: false,
        label: translate('custom.actions'),
        sortable: false
    }
]

class MyposTerminals extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            mypos_terminals: [],
            count: 0,
            skip: 0,
            limit: 50,
            limitOptions: [10, 50, 100, 250, 500],
            sort_key: '',
            sort_order: '',
            search: ''
        };
    }

    async componentDidMount() {
        this.getMyposTerminals();
    }

    async getMyposTerminals() {
        let options = {
            skip: this.state.skip,
            limit: this.state.limit
        }
        
        if (this.state.sort_key) {
            options.sort_key = this.state.sort_key;
            options.sort_order = this.state.sort_order;
        }

        if (this.state.search) {
            options.search = this.state.search;
        }

        let mypos_terminals = await getMyposTerminalList(options);
        this.setState({
            mypos_terminals: mypos_terminals.mypos_terminals,
            count: mypos_terminals.count
        });
    }

    async deleteMyposTerminalFromId(id) {
        let c = await Swal.fire({
            title: translate('custom.are_you_sure'),
            text: translate('custom.do_you_want_to_delete'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: translate('custom.yes'),
            cancelButtonText: translate('custom.no'),
            confirmButtonColor: 'red'
        });
        if (c.isConfirmed) {
            let response = await deleteMyposTerminal(id);
            if (response.error) {
                Swal.fire({
                    title: translate('custom.error'),
                    text: response.error_code ? convertMessageCodeToMessage(response.error_code) : response.error,
                    icon: 'error',
                    confirmButtonColor: config.baseColor
                });
                return;
            }

            Swal.fire({
                title: translate('custom.success'),
                text: response.message_code ? convertMessageCodeToMessage(response.message_code) : response.message,
                icon: 'success',
                timer: 2000,
                timerProgressBar: true,
                showConfirmButton: false
            });

            this.getMyposTerminals();
        }
    }

    render() {
        return (
            <V2Layout
                currentMenu='mypos_terminals'
            >

                {/* Add button */}
                <Grid container spacing={2} style={{marginBottom: '10px', justifyContent: 'flex-end'}}>
                    <Grid item>
                        <Button variant='contained' style={{textTransform: 'none'}}
                            onClick={() => {
                                this.props.history.push('/v2/admin/mypos_terminals/add');
                            }}
                        >
                            + {translate('custom.add')}
                        </Button>
                    </Grid>
                </Grid>

                <Grid container style={{marginBottom: '10px'}}>
                    <Card style={{width: '100%', padding: '20px'}}>
                        <Grid item xs={12}>
                            <TextField
                                id="search-input"
                                size='small'
                                fullWidth
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                label={translate('custom.search')}
                                variant="outlined"
                                value={this.state.search}
                                onChange={async (e) => {
                                    await this.setState({search: e.target.value});
                                    this.getMyposTerminals();
                                }}
                            />
                        </Grid>
                    </Card>
                </Grid>


                <TableContainer component={Card} style={{maxHeight: '80vh', overflow: 'auto'}}>
                    <Table stickyHeader size='small'>
                        <TableHead>
                            <TableRow>
                                {tableHeader.map((headCell) => (
                                    <TableCell
                                        key={headCell.key}
                                        align={headCell.align ? headCell.align : 'left'}
                                        padding={headCell.disablePadding ? 'none' : 'normal'}
                                        sortDirection={(this.state.sort_key === headCell.key) ? ((this.state.sort_order == 'ASC') ? 'asc' : 'desc') : false}
                                    >
                                        {(headCell.sortable) ? (
                                            <TableSortLabel
                                                active={this.state.sort_key === headCell.key}
                                                direction={this.state.sort_key === headCell.key ? ((this.state.sort_order == 'ASC') ? 'asc' : 'desc') : 'asc'}
                                                onClick={async () => {
                                                    await this.setState({
                                                        sort_key: headCell.key,
                                                        sort_order: this.state.sort_key === headCell.key ? (this.state.sort_order === 'ASC' ? 'DESC' : 'ASC') : 'ASC'
                                                    })
                                                    this.getMyposTerminals();
                                                }}
                                            >
                                                {headCell.label}
                                                {this.state.sort_key === headCell.key ? (
                                                    <Box component="span" sx={visuallyHidden}>
                                                        {this.state.sort_order === 'DESC' ? 'sorted descending' : 'sorted ascending'}
                                                    </Box>
                                                ) : null}
                                            </TableSortLabel>
                                        ) : (
                                            <>
                                                {headCell.label}
                                            </>
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {this.state.mypos_terminals.map((row) => (
                                <StyledTableRow key={row._id} hover
                                    onDoubleClick={() => {
                                        this.props.history.push('/v2/admin/mypos_terminals/' + row._id);
                                    }}
                                >
                                    <TableCell>{row.terminal_code}</TableCell>
                                    <TableCell>{row.terminal_id}</TableCell>
                                    <TableCell align="center">
                                        <IconButton size='small' disabled>
                                            {(row.is_paired) ? (
                                                <CheckCircleIcon style={{color: 'green'}} />
                                            ) : (
                                                <DangerousIcon style={{color: 'red'}} />
                                            )}
                                        </IconButton>
                                    </TableCell>
                                    <TableCell>
                                        <IconButton size='small' onClick={() => {
                                            this.props.history.push('/v2/admin/mypos_terminals/' + row._id);
                                        }}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton size='small' color='error' onClick={() => {
                                            this.deleteMyposTerminalFromId(row._id);
                                        }}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Grid container style={{width: '100%'}}>
                    <Grid item xs={12}> 
                        <TablePagination
                            component="div"
                            count={this.state.count}
                            page={this.state.skip / this.state.limit}
                            onPageChange={async (event, page) => {
                                await this.setState({
                                    skip: page * this.state.limit
                                })
                                this.getMyposTerminals();
                            }}
                            rowsPerPage={this.state.limit}
                            onRowsPerPageChange={async (event) => {
                                await this.setState({
                                    limit: event.target.value
                                })
                                this.getMyposTerminals();
                            }}
                            rowsPerPageOptions={this.state.limitOptions}
                        />
                    </Grid>
                </Grid>
            </V2Layout>
        )
    }
}

export default MyposTerminals;
